import React, { useEffect, useState } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
} from '@mui/material';
import { UserProfileService } from '../../services/user-profile.service';
import {
    ALL_PREFERRED_DISBURSEMENT_METHODS,
    IUserProfile,
    IUserProfileUpdate,
    PREFERRED_DISBURSEMENT_LABELS,
    PreferredDisbursementMethod,
} from '../../interfaces/user-profile.interface';
import { buildProfileClearFields } from '../../utils/profileClearFields';
import {
    isValidMobileWalletNumber,
    MOBILE_WALLET_INVALID_MESSAGE,
    MOBILE_PROVIDERS,
    isLegacyMobileProvider,
    LEGACY_PROVIDER_HINT,
} from '../../utils/mobileWallet';
import { useToast } from '../../context/ToastContext';
import { helpWhy } from '../../help/why';

interface Props {
    open: boolean;
    onClose: () => void;
    userId: string;
    displayName: string;
    onSaved?: () => void | Promise<void>;
}

interface BankingFormData {
    firstName: string;
    lastName: string;
    bankName: string;
    bankAccountNumber: string;
    bankRoutingNumber: string;
    bankBranch: string;
    mobileBankingProvider: string;
    mobileBankingAccount: string;
    employeeCode: string;
    preferredDisbursementMethod: PreferredDisbursementMethod | '';
}

const emptyForm: BankingFormData = {
    firstName: '',
    lastName: '',
    bankName: '',
    bankAccountNumber: '',
    bankRoutingNumber: '',
    bankBranch: '',
    mobileBankingProvider: '',
    mobileBankingAccount: '',
    employeeCode: '',
    preferredDisbursementMethod: '',
};

const EmployeeBankingDialog: React.FC<Props> = ({ open, onClose, userId, displayName, onSaved }) => {
    const toast = useToast();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [profile, setProfile] = useState<IUserProfile | null>(null);
    const [form, setForm] = useState<BankingFormData>(emptyForm);

    useEffect(() => {
        if (!open || !userId) return;
        setLoading(true);
        UserProfileService.getProfileByUserId(userId)
            .then((p) => {
                setProfile(p);
                setForm({
                    firstName: p.firstName || '',
                    lastName: p.lastName || '',
                    bankName: p.bankName || '',
                    bankAccountNumber: p.bankAccountNumber || '',
                    bankRoutingNumber: p.bankRoutingNumber || '',
                    bankBranch: p.bankBranch || '',
                    mobileBankingProvider: p.mobileBankingProvider || '',
                    mobileBankingAccount: p.mobileBankingAccount || '',
                    employeeCode: p.employeeCode || '',
                    preferredDisbursementMethod: p.preferredDisbursementMethod || '',
                });
            })
            .catch((err) => toast.showApiError(err, 'Failed to load banking details.'))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, userId]);

    const handleChange = (field: keyof BankingFormData) => (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        setForm((prev) => ({ ...prev, [field]: e.target.value }));
    };

    const handleSave = async () => {
        if (!profile) return;
        if (!isValidMobileWalletNumber(form.mobileBankingAccount)) return;
        setSaving(true);
        try {
            // Fix round 1 #3: the backend PUT is a full replace, and this dialog only ever holds
            // the profile snapshot from when it opened. If someone else (or the user themself,
            // elsewhere) changed the name/phone in the meantime, echoing the STALE profile values
            // here would silently overwrite that newer data with a banking-only save. Re-fetch
            // immediately before building the payload so email/firstName/lastName/phone always
            // come from the current record, not from dialog-open time.
            const current = await UserProfileService.getProfileByUserId(userId);

            const clearFields = buildProfileClearFields(current, form as unknown as Record<string, unknown>);
            const clearSet = new Set<string>(clearFields);

            let payload: IUserProfileUpdate = {
                email: current.email,
                firstName: current.firstName,
                lastName: current.lastName,
                phone: current.phone,
            };

            (Object.keys(emptyForm) as (keyof BankingFormData)[]).forEach((key) => {
                if (clearSet.has(key)) return;
                // Names are only sent when HR actually edited them: otherwise the freshly
                // re-fetched values above must win over the open-time snapshot.
                if (key === 'firstName' && form.firstName === (profile.firstName || '')) return;
                if (key === 'lastName' && form.lastName === (profile.lastName || '')) return;
                const value = form[key];
                if (value === '' || value === undefined) return;
                payload = { ...payload, [key]: value };
            });

            if (clearFields.length > 0) {
                payload = { ...payload, clearFields };
            }

            await UserProfileService.updateUserProfile(current.id, payload);
            toast.showSuccess('Banking details updated.');
            await onSaved?.();
            onClose();
        } catch (err) {
            toast.showApiError(err, 'Failed to update banking details.');
        } finally {
            setSaving(false);
        }
    };

    const saveWhy = saving ? null
        : loading ? 'The details are still loading. Wait a moment.'
        : !profile ? 'The details could not be loaded, so there is nothing to save. Close the window and open it again.'
        : !isValidMobileWalletNumber(form.mobileBankingAccount) ? 'The mobile banking account number is not valid. Use 11 digits starting 013 to 019, or leave it empty.'
        : null;

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" data-help-page="users.banking.page">
            <DialogTitle>Banking & disbursement — {displayName}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} data-help-section="users.banking.section.name">
                        <TextField
                            data-help="users.banking.field.firstName"
                            label="First name"
                            fullWidth
                            value={form.firstName}
                            onChange={handleChange('firstName')}
                            disabled={loading}
                        />
                        <TextField
                            data-help="users.banking.field.lastName"
                            label="Last name"
                            fullWidth
                            value={form.lastName}
                            onChange={handleChange('lastName')}
                            disabled={loading}
                        />
                    </Stack>
                    <TextField
                        data-help="users.banking.field.bankName"
                        label="Bank name"
                        fullWidth
                        value={form.bankName}
                        onChange={handleChange('bankName')}
                        disabled={loading}
                    />
                    <TextField
                        data-help="users.banking.field.bankAccountNumber"
                        label="Bank account number"
                        fullWidth
                        value={form.bankAccountNumber}
                        onChange={handleChange('bankAccountNumber')}
                        disabled={loading}
                    />
                    <TextField
                        data-help="users.banking.field.bankRoutingNumber"
                        label="Bank routing number"
                        fullWidth
                        value={form.bankRoutingNumber}
                        onChange={handleChange('bankRoutingNumber')}
                        disabled={loading}
                    />
                    <TextField
                        data-help="users.banking.field.bankBranch"
                        label="Bank branch"
                        fullWidth
                        value={form.bankBranch}
                        onChange={handleChange('bankBranch')}
                        disabled={loading}
                    />
                    <FormControl fullWidth disabled={loading} data-help="users.banking.field.mobileProvider">
                        <InputLabel id="mobile-provider-label">Mobile banking provider</InputLabel>
                        <Select
                            labelId="mobile-provider-label"
                            label="Mobile banking provider"
                            value={form.mobileBankingProvider}
                            onChange={(e) =>
                                setForm((prev) => ({ ...prev, mobileBankingProvider: e.target.value as string }))
                            }
                        >
                            <MenuItem value="">Not set</MenuItem>
                            {isLegacyMobileProvider(form.mobileBankingProvider) && (
                                <MenuItem value={form.mobileBankingProvider}>{form.mobileBankingProvider}</MenuItem>
                            )}
                            {MOBILE_PROVIDERS.map((m) => (
                                <MenuItem key={m} value={m}>{m}</MenuItem>
                            ))}
                        </Select>
                        {isLegacyMobileProvider(form.mobileBankingProvider) && (
                            <FormHelperText>{LEGACY_PROVIDER_HINT}</FormHelperText>
                        )}
                    </FormControl>
                    <TextField
                        data-help="users.banking.field.mobileAccount"
                        label="Mobile banking account"
                        fullWidth
                        value={form.mobileBankingAccount}
                        onChange={handleChange('mobileBankingAccount')}
                        disabled={loading}
                        error={!isValidMobileWalletNumber(form.mobileBankingAccount)}
                        helperText={
                            isValidMobileWalletNumber(form.mobileBankingAccount)
                                ? undefined
                                : MOBILE_WALLET_INVALID_MESSAGE
                        }
                    />
                    <TextField
                        data-help="users.banking.field.employeeCode"
                        label="Employee code"
                        fullWidth
                        value={form.employeeCode}
                        onChange={handleChange('employeeCode')}
                        disabled={loading}
                    />
                    <FormControl fullWidth disabled={loading} data-help="users.banking.field.disbursement">
                        <InputLabel id="preferred-disbursement-label">Preferred disbursement</InputLabel>
                        <Select
                            labelId="preferred-disbursement-label"
                            label="Preferred disbursement"
                            value={form.preferredDisbursementMethod}
                            onChange={(e) =>
                                setForm((prev) => ({
                                    ...prev,
                                    preferredDisbursementMethod: e.target.value as PreferredDisbursementMethod | '',
                                }))
                            }
                        >
                            <MenuItem value="">Not set</MenuItem>
                            {ALL_PREFERRED_DISBURSEMENT_METHODS.map((m) => (
                                <MenuItem key={m} value={m}>
                                    {PREFERRED_DISBURSEMENT_LABELS[m]}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving} data-help="users.banking.cancel">Cancel</Button>
                <Button
                    data-help="users.banking.save"
                    {...helpWhy(saveWhy ? { kind: 'status', message: saveWhy } : null)}
                    onClick={handleSave}
                    variant="contained"
                    disabled={loading || saving || !profile || !isValidMobileWalletNumber(form.mobileBankingAccount)}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EmployeeBankingDialog;
